import { compress } from '../camera'
import * as db from './db'

/**
 * Хранение снимков приёмки.
 *
 * Файл, выбранный камерой, немедленно пишется в IndexedDB. Не «после
 * успешной отправки» и не «когда появится сеть»: между выбором файла
 * и отправкой водитель может свернуть приложение, потерять связь или
 * закрыть вкладку — и вся работа исчезнет.
 *
 * Превью тоже строится из сохранённого Blob, а не из исходного File:
 * File живёт только пока жив компонент.
 */

export const STATUS_LOCAL = 'local' // сохранено на устройстве
export const STATUS_SENDING = 'sending' // отправляется
export const STATUS_SENT = 'sent' // подтверждено сервером
export const STATUS_FAILED = 'failed' // не удалось после всех попыток

// ── Политика квот ───────────────────────────────────────────
//
// IndexedDB не бесконечна, а на iOS ещё и вытесняется системой.
//
// Квоты раздельны по назначению (scope): сотня фотографий накладных
// в чате не должна вытеснить шесть снимков незавершённой приёмки —
// это данные разной ценности, и «общий котёл» однажды удалил бы
// невосстановимое ради восстановимого.

export const SCOPE_TRIP = 'trip'
export const SCOPE_CHAT = 'chat'

export const QUOTAS = {
  // Больше не помещается в разумную приёмку: шесть на старт, три
  // на финиш. Дольше недели незавершённую приёмку уже не завершат.
  [SCOPE_TRIP]: { maxPhotos: 40, maxAgeDays: 7 },
  // Чат: отправленное удаляется сразу после подтверждения сервером,
  // здесь задерживаются только неотправленные — им даётся больше
  // времени пережить долгий рейс без связи.
  [SCOPE_CHAT]: { maxPhotos: 200, maxAgeDays: 14 },
}

/** Совместимость с записями до появления scope. */
const scopeOf = (photo) => photo.scope || SCOPE_TRIP

/** Больше не помещается в разумную приёмку: шесть на старт, три на финиш. */
export const MAX_PHOTOS = QUOTAS[SCOPE_TRIP].maxPhotos
/** Дольше приёмка не живёт: незавершённую за неделю уже не завершат. */
export const MAX_AGE_DAYS = QUOTAS[SCOPE_TRIP].maxAgeDays
/** Порог предупреждения о нехватке места. */
export const QUOTA_WARN_RATIO = 0.8

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ?? `p-${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Отпечаток содержимого.
 *
 * Нужен, чтобы отличить «переснял тот же кадр» от «переснял заново»:
 * повторная отправка идентичного файла — лишний трафик на мобильной
 * сети, где он и так дорог.
 */
async function checksum(blob) {
  if (!globalThis.crypto?.subtle) return null
  try {
    const buffer = await blob.arrayBuffer()
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer)
    return Array.from(new Uint8Array(digest.slice(0, 8)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

export const photoId = (draftId, phase, category) => `${draftId}:${phase}:${category}`

/**
 * Сохранить снимок.
 *
 * Сжатие происходит здесь, до записи: несжатый снимок 12 Мп занимает
 * 3–5 МБ, шесть таких не влезают ни в лимит запроса, ни в разумную
 * квоту хранилища.
 */
export async function savePhoto({ draftId, phase, category, file, scope = SCOPE_TRIP, preset = null }) {
  // Пресет сжатия: документы (накладные, ТОРГ-2) жмутся мягче —
  // читаемость текста важнее веса.
  const blob = preset
    ? await compress(file, preset.maxEdge, preset.quality)
    : await compress(file)
  const record = {
    id: photoId(draftId, phase, category),
    draftId,
    phase,
    category,
    scope,
    blob,
    size: blob.size,
    type: blob.type || 'image/jpeg',
    checksum: await checksum(blob),
    createdAt: new Date().toISOString(),
    status: STATUS_LOCAL,
    attempts: 0,
    error: null,
  }

  await db.put(db.STORE_PHOTOS, record)
  return record
}

export const getPhoto = (draftId, phase, category) =>
  db.get(db.STORE_PHOTOS, photoId(draftId, phase, category))

export async function listPhotos(draftId, phase) {
  const all = await db.getAllBy(db.STORE_PHOTOS, 'draft', [draftId, phase])
  return all || []
}

export async function setStatus(id, status, error = null) {
  const record = await db.get(db.STORE_PHOTOS, id)
  if (!record) return null
  record.status = status
  record.error = error
  if (status === STATUS_FAILED) record.attempts = (record.attempts || 0) + 1
  await db.put(db.STORE_PHOTOS, record)
  return record
}

export const deletePhoto = (id) => db.remove(db.STORE_PHOTOS, id)

/** Удалить все снимки черновика. Вызывается после успешного старта. */
export async function deleteDraft(draftId) {
  const all = (await db.getAll(db.STORE_PHOTOS)) || []
  const mine = all.filter((p) => p.draftId === draftId)
  await Promise.all(mine.map((p) => db.remove(db.STORE_PHOTOS, p.id)))
  return mine.length
}

/**
 * Очистка по политике квот — раздельно по scope.
 *
 * Порядок внутри scope важен: сначала отправленное (оно уже
 * на сервере), потом старое. Незавершённое свежее удаляется
 * последним — это единственное, что нельзя восстановить.
 * Переполнение чата НИКОГДА не трогает фото приёмки, и наоборот.
 */
export async function enforceQuota() {
  const all = (await db.getAll(db.STORE_PHOTOS)) || []
  if (all.length === 0) return { removed: 0 }

  const now = Date.now()
  const removed = []

  for (const [scope, policy] of Object.entries(QUOTAS)) {
    const mine = all.filter((p) => scopeOf(p) === scope)
    if (mine.length === 0) continue

    const cutoff = now - policy.maxAgeDays * 86_400_000
    const gone = []

    for (const photo of mine) {
      if (photo.status === STATUS_SENT) gone.push(photo)
      else if (new Date(photo.createdAt).getTime() < cutoff) gone.push(photo)
    }

    let survivors = mine.filter((p) => !gone.includes(p))
    if (survivors.length > policy.maxPhotos) {
      survivors = survivors.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      )
      gone.push(...survivors.slice(0, survivors.length - policy.maxPhotos))
    }

    removed.push(...gone)
  }

  await Promise.all(removed.map((p) => db.remove(db.STORE_PHOTOS, p.id)))
  return { removed: removed.length }
}

/**
 * Состояние хранилища.
 *
 * Предупреждать водителя нужно ДО того, как место кончится:
 * после — снимок уже потерян, и сообщение бесполезно.
 */
export async function storageStatus() {
  const stored = (await db.count(db.STORE_PHOTOS)) || 0
  let usage = 0
  let quota = 0

  try {
    const estimate = await globalThis.navigator?.storage?.estimate?.()
    usage = estimate?.usage || 0
    quota = estimate?.quota || 0
  } catch {
    /* не во всех браузерах доступно */
  }

  const ratio = quota ? usage / quota : 0
  return {
    photos: stored,
    usage,
    quota,
    ratio,
    isLow: ratio >= QUOTA_WARN_RATIO || stored >= MAX_PHOTOS,
  }
}

/** URL для превью. Освобождать обязан вызывающий. */
export function previewUrl(record) {
  if (!record?.blob) return null
  return URL.createObjectURL(record.blob)
}
